import { Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, Subject, takeUntil } from 'rxjs';
import { ECoachChatSenderType } from '@gray/domain/models/coach/chat-message.model';
import { CoachChatService } from '../../core/services/coach-chat.service';
import { CoachChatSocketService, ICoachChatEvent } from '../../core/services/coach-chat-socket.service';
import { ICoachConversationSummary } from '../../core/models/coach-conversation.model';

const PAGE_SIZE = 20;

/**
 * The coach's inbox: every current student on the left, newest conversation first, and the
 * selected conversation on the right. Below lg only one side shows at a time — the list, or the
 * conversation with a way back. The selected student lives in the URL so a conversation can be
 * linked to and the browser's back button returns to the list.
 */
@Component({
  selector: 'coach-messages',
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.scss',
})
export class MessagesComponent implements OnInit, OnDestroy {
  /** Route parameter of `messages/:studentId`. */
  @Input() set studentId(value: string) {
    const parsed = Number(value);
    this.selectedStudentId = Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  selectedStudentId: number | null = null;

  conversations: ICoachConversationSummary[] = [];
  page = 1;
  lastPage = 1;
  isLoading = true;

  senderTypes = ECoachChatSenderType;

  fg: FormGroup = this.formBuilder.group({
    search: [null],
    onlyUnread: [false],
  });

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private chatService: CoachChatService,
    private chatSocket: CoachChatSocketService,
    private translate: TranslateService,
    private router: Router,
    @Inject(LOCALE_ID) private locale: string,
  ) {
  }

  ngOnInit(): void {
    this.watchFilters();
    this.listenSocket();
    this.getConversations();
  }

  // region List
  getConversations(append = false) {
    this.isLoading = true;
    this.chatService.getConversations(this.buildQueryString())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const rows = (r.data?.result ?? []) as ICoachConversationSummary[];
        this.conversations = append ? [...this.conversations, ...rows] : rows;
        this.lastPage = r.data?.meta?.lastPage ?? 1;
      });
  }

  hasMore(): boolean {
    return this.page < this.lastPage;
  }

  loadMore() {
    this.page += 1;
    this.getConversations(true);
  }

  reload() {
    this.page = 1;
    this.getConversations();
  }

  private buildQueryString(): string {
    const params = new URLSearchParams({page: `${this.page}`, size: `${PAGE_SIZE}`});
    const search = (this.fg.value.search ?? '').trim();
    if (search) {
      params.set('search', search);
    }
    if (this.fg.value.onlyUnread) {
      params.set('filter[unread]', '1');
    }
    return `?${params.toString()}`;
  }

  private watchFilters() {
    this.fg.get('search').valueChanges
      .pipe(takeUntil(this.destroyed$), debounceTime(400), distinctUntilChanged())
      .subscribe(() => this.reload());

    this.fg.get('onlyUnread').valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.reload());
  }
  // endregion

  // region Selection
  openConversation(row: ICoachConversationSummary) {
    this.router.navigate(['/dashboard/messages', row.student.id]);
  }

  backToList() {
    this.router.navigate(['/dashboard/messages']);
  }

  selectedName(): string | null {
    return this.conversations.find(row => row.student.id === this.selectedStudentId)?.student.name ?? null;
  }

  /** The messages tab reports what it found and then 0 once it has marked the conversation read. */
  onUnreadChanged(count: number) {
    const row = this.conversations.find(current => current.student.id === this.selectedStudentId);
    if (row) {
      row.unreadCount = count;
    }
  }
  // endregion

  // region Live updates
  private listenSocket() {
    this.chatSocket.onEvent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: ICoachChatEvent) => {
        if (event?.event === 'message') {
          this.onMessage(event.data);
        }
        if (event?.event === 'read' && event.data?.side === ECoachChatSenderType.Coach) {
          const row = this.conversations.find(current => current.student.id === event.data.studentUserId);
          if (row) {
            row.unreadCount = 0;
          }
        }
      });
  }

  /** Moves the conversation to the top with its new head; a student not loaded yet means a reload. */
  private onMessage(data: any) {
    const row = this.conversations.find(current => current.student.id === data?.studentUserId);
    if (!row) {
      if (!this.fg.value.search && !this.fg.value.onlyUnread) {
        this.reload();
      }
      return;
    }

    const fromStudent = data.senderType === ECoachChatSenderType.Student;
    const updated: ICoachConversationSummary = {
      ...row,
      conversationId: data.conversationId ?? row.conversationId,
      lastMessage: {body: data.body, senderType: data.senderType, createdAt: data.createdAt},
      lastMessageAt: data.createdAt,
      // The open conversation reads its messages as they arrive.
      unreadCount: (fromStudent && row.student.id !== this.selectedStudentId) ? row.unreadCount + 1 : row.unreadCount,
    };

    this.conversations = [updated, ...this.conversations.filter(current => current !== row)];
  }
  // endregion

  // region Display
  /** Clock time today, the "yesterday" label, day and month this year, a full date before that. */
  timeLabel(value: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return formatDate(date, 'HH:mm', this.locale);
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return this.translate.instant('messages.yesterday');
    }
    if (date.getFullYear() === today.getFullYear()) {
      return formatDate(date, 'd MMM', this.locale);
    }
    return formatDate(date, 'dd.MM.yyyy', this.locale);
  }

  isOwnLastMessage(row: ICoachConversationSummary): boolean {
    return row.lastMessage?.senderType === ECoachChatSenderType.Coach;
  }

  badgeLabel(count: number): string {
    return count > 9 ? '9+' : `${count}`;
  }
  // endregion

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
